import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { merge } from 'lodash-es'
import type { DeepPartial } from 'ts-essentials'

import type {
  Activity,
  CascadeActivity,
  ActivityId,
  Category,
  ActivityType,
} from '@/types'

import type { Options } from './BaseService.types'
import type { ActivityFilter } from './ActivityService.types'

import BaseService from './BaseService'

export default class ActivityService extends BaseService<
  Activity,
  CascadeActivity,
  ActivityFilter
> {
  constructor(client: AxiosInstance) {
    super(client, 'activity')
  }

  get<R extends DeepPartial<Activity> = Activity>(
    id: ActivityId,
    config?: AxiosRequestConfig & Options
  ): Promise<R> {
    let merged = config ?? {}
    if (config?.relations === true) {
      merged = merge({}, merged, {
        params: {
          join: [
            ActivityService.STEPS_RELATION,
            ActivityService.MEDIA_RELATION,
            ActivityService.MEDIA_MEDIA_RELATION,
            ActivityService.STEP_MEDIA_RELATION,
            ActivityService.STEP_MEDIA_MEDIA_RELATION,
            `${ActivityService.UPDATED_BY}||id,fullName,avatar`,
          ],
        },
      })
    }
    return super.get(id, merged)
  }

  async copy(id: ActivityId, name: string): Promise<Activity> {
    const { data } = await this.client.post<Activity>(`/${this.entity}/${id}`, {
      name,
    })
    return data
  }

  async copyMany(ids: ActivityId[]): Promise<ActivityId[]> {
    const { data } = await this.client.post<ActivityId[]>(
      `/${this.entity}/bulk`,
      {
        ids,
      }
    )
    return data
  }

  async generate(payload: {
    categories: Record<string, Category>
    round: string
    type: ActivityType
  }) {
    const { data } = await this.client.post(
      `/${this.entity}/ai-sample`,
      payload
    )
    return data
  }

  protected s(filter: ActivityFilter, params: Record<string, unknown>) {
    if (filter.scoped !== undefined) {
      params.scoped = { $eq: filter.scoped }
    }
    if (filter.status !== undefined) {
      params.status = { $eq: filter.status }
    }
    if (filter.type !== undefined) {
      params.type = { $eq: filter.type }
    }
  }

  static STEPS_RELATION = 'steps'
  static MEDIA_RELATION = 'media'
  static MEDIA_MEDIA_RELATION = 'media.media'
  static STEP_MEDIA_RELATION = 'steps.media'
  static STEP_MEDIA_MEDIA_RELATION = 'steps.media.media'
  static ACTIVITY_GROUPS_RELATION = 'activityGroups'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_RELATION =
    'activityGroups.activityGroup'
  static ROOMS_RELATION = 'rooms'
  static ROOMS_ROOM_RELATION = 'rooms.room'
  static UPDATED_BY = 'updatedBy'
}
